import { useEffect, useState } from "react";

import { api, errMsg, errStatus } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { useToast } from "../../../context/ToastContext.js";
import { AsyncButton } from "../../../components/ui/async-button.js";
import { Textarea } from "../../../components/ui/textarea.js";
import { Badge } from "../../../components/ui/badge.js";
import { LockedNotice, SectionCard, fmtDate } from "../shared.js";
import type { ReviewDetail, TemporaryManagerDelegation } from "../types.js";

// Temporary movement panel (spec 4.D): ONE card listing every temporary-manager
// period on this review, newest first — who covered, for which dates, and the
// report they wrote for that period. HR and the line manager see every period;
// a temporary manager sees only their own (the API filters). The current
// temporary manager writes and submits their report inside their own entry.
// Granting and revoking live on the review list's team rows (spec 3 & 13).
export default function TemporaryManagerSection({
  review,
  onChanged,
}: {
  review: ReviewDetail;
  onChanged: () => void;
}) {
  const caps = review.capabilities;
  const periods = review.temporaryManagers;
  const canSeePanel = caps.isLineManager || caps.isHR || caps.isSuperuser || caps.isTemporaryManager;
  if (!canSeePanel || periods.length === 0) return null;

  return (
    <SectionCard
      collapsible
      title="Temporary Manager Support"
      description="Every temporary manager on this review, with the period they covered and their report. Assign or revoke from the review list's team rows."
      actions={
        <Badge tone="neutral">
          {periods.length} period{periods.length === 1 ? "" : "s"}
        </Badge>
      }
    >
      <ol className="flex flex-col gap-3">
        {periods.map((p) => (
          <li key={p.id}>
            <PeriodEntry
              period={p}
              // Only the current temporary manager, on their own active period, edits.
              editable={!!review.temporaryManager && review.temporaryManager.id === p.id && caps.canEditTemporaryManagerComment}
              canSubmit={caps.canSubmitTemporaryManagerComment}
              onChanged={onChanged}
            />
          </li>
        ))}
      </ol>
    </SectionCard>
  );
}

function statusBadge(p: TemporaryManagerDelegation) {
  if (p.revokedAt) return <Badge tone="warning">Revoked {fmtDate(p.revokedAt)}</Badge>;
  if (p.isActive) return <Badge tone="info">Active</Badge>;
  return <Badge tone="neutral">Ended</Badge>;
}

// One temporary-manager period: name, dates, status, and its report.
function PeriodEntry({
  period: p,
  editable,
  canSubmit,
  onChanged,
}: {
  period: TemporaryManagerDelegation;
  editable: boolean;
  canSubmit: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const [comment, setComment] = useState(p.comment ?? "");

  useEffect(() => {
    setComment(p.comment ?? "");
  }, [p.id, p.comment]);

  // Access was revoked/expired, or the report was already submitted, since this
  // page loaded — reconcile so the stale editor clears instead of lingering.
  function reconcileOnConflict(e: unknown): boolean {
    const status = errStatus(e);
    if (status === 403 || status === 409) {
      toast.show(errMsg(e) ?? "This temporary-manager report can no longer be changed.", "error");
      onChanged();
      return true;
    }
    return false;
  }

  async function saveDraft() {
    try {
      await api.put(`/performance/delegations/${p.id}/comment`, { comment: comment || null });
      toast.show("Draft saved", "success");
      onChanged();
    } catch (e) {
      logError("TemporaryManagerSection.saveDraft", e);
      if (!reconcileOnConflict(e)) toast.show(errMsg(e) ?? "Could not save the draft", "error");
      throw e;
    }
  }

  async function submitReport() {
    if (!comment.trim()) {
      toast.show("Write your report before submitting.", "error");
      throw new Error("validation");
    }
    try {
      await api.post(`/performance/delegations/${p.id}/submit`, { comment: comment.trim() });
      toast.show("Report submitted", "success");
      onChanged();
    } catch (e) {
      logError("TemporaryManagerSection.submitReport", e);
      if (!reconcileOnConflict(e)) toast.show(errMsg(e) ?? "Could not submit the report", "error");
      throw e;
    }
  }

  return (
    <div className="rounded-md border border-surface-200 p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-[14px] font-semibold">{p.delegateName}</p>
          <p className="text-[12px] text-surface-500">
            {fmtDate(p.startDate)} – {p.endDate ? fmtDate(p.endDate) : "Open-ended"} · Assigned by {p.grantedByName}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {statusBadge(p)}
          {p.commentSubmittedAt ? (
            <Badge tone="success">Report submitted {fmtDate(p.commentSubmittedAt)}</Badge>
          ) : (
            <Badge tone="warning">Report pending</Badge>
          )}
        </div>
      </div>

      {p.commentSubmittedAt ? (
        <p className="text-[13px] whitespace-pre-wrap">{p.comment || <span className="text-surface-400">—</span>}</p>
      ) : editable ? (
        <>
          <p className="mb-1 text-[12px] text-surface-500">
            Save a draft as you go, then Submit to finalise. Once submitted it locks and the line manager can remove your
            access.
          </p>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
          <div className="mt-2 flex justify-end gap-2">
            <AsyncButton variant="outline" animated={false} size="sm" onClick={() => saveDraft()}>
              Save draft
            </AsyncButton>
            <AsyncButton
              size="sm"
              disabled={!comment.trim() || !canSubmit}
              onClick={() => submitReport()}
              confirm={{
                title: "Submit your report?",
                description: "Once submitted you can no longer edit it, and the line manager can remove your access.",
                actionText: "Submit report",
              }}
            >
              Submit report
            </AsyncButton>
          </div>
        </>
      ) : (
        <LockedNotice>
          {p.isActive
            ? "The temporary manager has not submitted a report for this period yet."
            : "No report was submitted for this period."}
        </LockedNotice>
      )}
    </div>
  );
}
